import { useLiveQuery } from 'dexie-react-hooks'
import { v4 as uuidv4 } from 'uuid'
import { db, type Note } from '../db/dexie'
import { deleteImagesForNote } from '../db/images'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { logger } from '../lib/logger'
import { captureError } from '../lib/errorReporting'

const log = logger('Notes')

/**
 * Track which note IDs have been modified in this tab.
 * Used by pushDirtyNotes to only push notes this tab edited,
 * avoiding concurrent pushes of the same note from multiple tabs.
 */
export const locallyModifiedNoteIds = new Set<string>()

/**
 * Strip HTML tags from content using regex.
 * Avoids DOMParser overhead (expensive for search which calls this per note per keystroke)
 * and avoids triggering image loads from custom URL schemes (yellowpad://).
 */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim()
}

/**
 * Get display title from note content.
 * Uses regex to extract text from the first block element — avoids DOMParser
 * overhead since this runs on every keystroke for the toolbar title.
 */
const FIRST_BLOCK_RE = /<(?:p|h[1-6]|li|blockquote)[^>]*>(.*?)<\/(?:p|h[1-6]|li|blockquote)>/i

export function getDisplayTitle(note: Pick<Note, 'content' | 'created_at' | 'custom_title'>): string {
  // If user has set a custom title, always use it
  if (note.custom_title) {
    return note.custom_title.length > 50
      ? note.custom_title.substring(0, 50) + '...'
      : note.custom_title
  }

  if (note.content) {
    const match = note.content.match(FIRST_BLOCK_RE)
    const firstLine = match
      ? match[1].replace(/<[^>]*>/g, '').trim()
      : note.content.replace(/<[^>]*>/g, '').split('\n')[0].trim()
    if (firstLine) {
      return firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine
    }
  }
  return `Note ${new Date(note.created_at).toLocaleDateString()}`
}

/**
 * Create a new note
 */
export async function createNote(): Promise<Note> {
  const now = new Date().toISOString()
  const note: Note = {
    id: uuidv4(),
    content: '',
    custom_title: null,
    created_at: now,
    updated_at: now,
    is_deleted: false,
    is_pinned: false,
    synced_at: null,
    user_id: null,
    version: 1,
  }
  await db.notes.add(note)
  locallyModifiedNoteIds.add(note.id)
  return note
}

/**
 * Update note content
 * Dirty detection relies on updated_at > synced_at (no need to null out synced_at)
 * Only updates updated_at and bumps version if content actually changed.
 */
export async function updateNote(id: string, content: string): Promise<Note | undefined> {
  const note = await db.notes.get(id)
  if (!note) return

  // Skip if content hasn't changed — prevents marking the note dirty
  // when the editor fires onChange during content loading.
  if (note.content === content) return note

  // Log what changed to help debug phantom dirty notes
  const oldLen = note.content?.length ?? 0
  const newLen = content?.length ?? 0
  log.debug('updateNote %s: content changed (%d -> %d chars)', id.slice(0, 8), oldLen, newLen)

  const now = new Date().toISOString()
  await db.notes.update(id, {
    content,
    updated_at: now,
  })
  locallyModifiedNoteIds.add(id)
  return db.notes.get(id)
}

/**
 * Update note custom title.
 * Pass a non-empty string to set a custom title, or null to revert to auto-generated.
 * When a custom title is set, it won't change automatically as content changes.
 */
export async function updateNoteTitle(id: string, customTitle: string | null): Promise<Note | undefined> {
  const note = await db.notes.get(id)
  if (!note) return

  const trimmed = customTitle?.trim() || null
  if (note.custom_title === trimmed) return note

  const now = new Date().toISOString()
  await db.notes.update(id, {
    custom_title: trimmed,
    updated_at: now,
  })
  locallyModifiedNoteIds.add(id)
  return db.notes.get(id)
}

/**
 * Toggle pin status of a note
 */
export async function togglePinNote(id: string): Promise<Note | undefined> {
  const note = await db.notes.get(id)
  if (!note) return

  const now = new Date().toISOString()
  await db.notes.update(id, {
    is_pinned: !note.is_pinned,
    updated_at: now,
  })
  locallyModifiedNoteIds.add(id)
  return db.notes.get(id)
}

/**
 * Soft delete a note
 * Sets is_deleted = true for sync capability
 */
export async function deleteNote(id: string): Promise<void> {
  const now = new Date().toISOString()
  await db.notes.update(id, {
    is_deleted: true,
    updated_at: now,
  })
  locallyModifiedNoteIds.add(id)
  // Clean up associated images from IndexedDB
  await deleteImagesForNote(id)
}

/**
 * Permanently delete ALL notes.
 *
 * If sync is enabled (user is signed in), deletes from the server first,
 * then clears local IndexedDB. If sync is not enabled, just clears local
 * IndexedDB.
 *
 * @param userId - The authenticated user's ID (null if not authenticated)
 * @param isSyncEnabled - Whether sync is active (signed in)
 */
export async function deleteAllNotes(userId?: string | null, isSyncEnabled?: boolean): Promise<void> {
  // If sync is enabled, delete from server first
  if (isSyncEnabled && userId && isSupabaseConfigured) {
    // Fetch image storage paths before deleting metadata (needed for async storage cleanup)
    const { data: imageRecords } = await supabase
      .from('images')
      .select('storage_path')
      .eq('user_id', userId)
    const storagePaths: string[] = (imageRecords ?? []).map(r => r.storage_path).filter(Boolean)

    // Delete images metadata from server
    const { error: imgError } = await supabase
      .from('images')
      .delete()
      .eq('user_id', userId)

    if (imgError) {
      captureError(new Error(imgError.message), { source: 'notes.deleteAll.serverImages', extra: { userId } })
      throw new Error(`Failed to delete images from server: ${imgError.message}`)
    }

    // Delete notes from server
    const { error: noteError } = await supabase
      .from('notes')
      .delete()
      .eq('user_id', userId)

    if (noteError) {
      captureError(new Error(noteError.message), { source: 'notes.deleteAll.serverNotes', extra: { userId } })
      throw new Error(`Failed to delete notes from server: ${noteError.message}`)
    }

    log.info('Deleted all notes and images from server for user %s', userId)

    // Async cleanup: delete image files from Supabase Storage (fire-and-forget, non-blocking)
    if (storagePaths.length > 0) {
      supabase.storage
        .from('note-images')
        .remove(storagePaths)
        .then(({ error }) => {
          if (error) {
            captureError(new Error(error.message), { source: 'notes.deleteAllStorageCleanup', extra: { count: storagePaths.length } })
          } else {
            log.info('Cleaned up %d image files from storage', storagePaths.length)
          }
        })
    }
  }

  // Clear local IndexedDB
  await db.notes.clear()
  await db.images.clear()
  locallyModifiedNoteIds.clear()

  log.info('Cleared all local notes and images')
}

/**
 * Get a single note by ID
 */
export async function getNote(id: string): Promise<Note | undefined> {
  return db.notes.get(id)
}

/**
 * Hook to get all active notes (not deleted), sorted by updated_at desc
 */
export function useNotes() {
  const notes = useLiveQuery(
    () => db.notes
      .filter(note => !note.is_deleted)
      .sortBy('updated_at')
      .then(notes => notes.reverse()),
    []
  )

  return notes ?? []
}

/**
 * Hook to get a single note by ID
 */
export function useNote(id: string | null) {
  return useLiveQuery(
    () => (id ? db.notes.get(id) : undefined),
    [id]
  )
}

/**
 * Search notes by content
 */
export function useSearchNotes(query: string) {
  const notes = useLiveQuery(
    () => {
      if (!query.trim()) {
        return db.notes
          .filter(note => !note.is_deleted)
          .sortBy('updated_at')
          .then(notes => notes.reverse())
      }

      const lowerQuery = query.toLowerCase()
      return db.notes
        .filter(note =>
          !note.is_deleted &&
          stripHtml(note.content).toLowerCase().includes(lowerQuery)
        )
        .sortBy('updated_at')
        .then(notes => notes.reverse())
    },
    [query]
  )

  return notes ?? []
}
